import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { ldScript, serviceSchema } from "@/lib/schema";
import { PageLayout } from "@/components/site/PageLayout";
import {
  FeatureGrid,
  DeliverablesList,
  ProcessSteps,
  ServiceCta,
} from "@/components/site/ServiceBlocks";
import svcBattery from "@/assets/hero-battery.jpg";
import batteryDeliverables from "@/assets/battery-deliverables.jpg";
import batteryProcess from "@/assets/battery-process.jpg";

export const Route = createFileRoute("/energy-storage")({
  head: () => ({
    ...pageHead({
      title: "Energy Storage System Design · SunSmart Engineering",
      description:
        "Battery storage design and configuration for home backup, commercial demand management and grid-tied solar. Plans built to current codes for permitting.",
      path: "/energy-storage",
    }),
    scripts: [
      ldScript(
        serviceSchema({
          name: "Energy Storage System Design",
          description:
            "Battery storage system design and configuration for residential backup, commercial demand management, and grid-tied solar integration.",
          url: "/energy-storage",
          serviceType: "Energy Storage Engineering",
        }),
      ),
    ],
  }),
  component: EnergyStoragePage,
});

function EnergyStoragePage() {
  return (
    <PageLayout
      kicker="Renewables & Resiliency · Energy Storage"
      title="Energy Storage System Design"
      intro="Energy storage is an essential component of modern solar installations, providing reliability, resilience, and flexibility. At SunSmart Engineering, we offer professional design services to seamlessly integrate energy storage solutions into your solar projects. Whether for backup power, grid independence, or peak demand management, our designs optimize system performance and ensure safety."
    >
      <DeliverablesList
        label="01"
        title={
          <>
            Areas of <span className="text-sun">expertise.</span>
          </>
        }
        image={batteryDeliverables}
        imageAlt="Interior of an energy storage cabinet with organized wiring"
        items={[
          { title: "Whole Home and Partial Backup Systems", description: "Designs tailored to ensure uninterrupted power supply during outages, keeping critical systems running when you need them most." },
          { title: "Coupled Energy Storage Solutions", description: "Integration of energy storage with solar PV systems for enhanced energy efficiency and self-consumption." },
          { title: "Custom System Sizing", description: "Accurate sizing of battery systems to meet your project's specific energy needs, ensuring cost-efficiency and optimal performance." },
          { title: "Commercial and Industrial Backup Systems", description: "System design for large-scale commercial and industrial battery backup systems to fight demand charges and provide dependable backup power for critical systems." },
        ]}
      />

      <FeatureGrid
        label="02"
        title={
          <>
            Why choose SunSmart for <span className="text-sun">energy storage design?</span>
          </>
        }
        columns={4}
        items={[
          { title: "Tailored Designs", description: "Every project is unique, and our solutions are customized to match your energy storage requirements, whether for residential, commercial, or industrial applications." },
          { title: "Safety and Compliance", description: "We prioritize safety, ensuring that all designs meet local regulations and industry standards for secure, reliable operation." },
          { title: "Advanced Technology Expertise", description: "Our team is experienced in the latest energy storage technologies, including lithium-ion batteries, flow batteries, and hybrid systems." },
          { title: "Optimized System Performance", description: "With a focus on efficiency and longevity, we design systems that deliver consistent, dependable energy storage." },
        ]}
      />

      <ProcessSteps
        label="03"
        title={
          <>
            Tackle <span className="text-sun">any project.</span>
          </>
        }
        intro="Energy storage is transforming the solar industry, and SunSmart Engineering is here to help you stay ahead. With our precise designs and expert support, you can confidently integrate energy storage into your projects."
        steps={[
          { title: "Evaluate load & goals", description: "We assess backup needs, demand charges, and grid conditions to define system requirements." },
          { title: "Size & configure", description: "Battery selection, sizing, and configuration are engineered for peak performance and safety." },
          { title: "Seal & deliver", description: "Stamped plans meet the demands of today and prepare for the challenges of tomorrow." },
        ]}
      />

      <ServiceCta
        label="04"
        title={
          <>
            Ready to add <span className="text-sun">storage?</span>
          </>
        }
        intro="Confidently integrate energy storage into your projects with precise designs and expert support from SunSmart Engineering."
        image={batteryProcess}
        imageAlt="Technician wiring a residential energy storage system"
      />
    </PageLayout>
  );
}
